import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../utils/variables';
import { Point2d } from '../../../viewer/utilities/math-helpers';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { ProgressController } from '../../utils/progress-controller';
import { PromiseAbortController } from '../../utils/promise-abort-controller';
import { Button } from '../button/button';
import { Dialog } from '../dialog/dialog';
import { ProgressBar } from '../progress-bar/progress-bar';

interface Props {
  callback?: () => Promise<void> | void;
  progressController: ProgressController;
  abortController?: PromiseAbortController;
  onClose: () => void;
  title: string;
  position?: Point2d;
  width?: number;
  initialValue?: string;
  valueType?: 'text' | 'number';
  children?: React.ReactNode;
  disabled?: boolean;
}

export function useProgressDialogDependencies(numberOfSteps: number) {
  const [showDialog, setShowDialog] = React.useState(false);
  const abortController = React.useRef(new PromiseAbortController());
  const controller = React.useRef(new ProgressController(undefined, numberOfSteps));

  return {
    showDialog,
    setShowDialog,
    abortController,
    controller,
  };
}

/**
 * A simple dialog with a progress bar that will automatically close when the process has finished.
 */
const ProgressDialog = (props: Props) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [progressText, setProgressText] = React.useState(
    t('progressDialog.defaultProgressText', { ns: 'components' }) as string,
  );
  const { handleError, buildErrorList } = useErrorHandling({ displayNonApiErrors: true });

  const onProgress = (amount: number, text?: string) => {
    setProgress(amount);
    if (text) {
      setProgressText(text);
    }
  };

  const hasRunRef = React.useRef(false);

  React.useEffect(() => {
    // Guard against StrictMode's mount→unmount→mount cycle and any future remount that
    // would otherwise re-invoke the one-shot callback. Empty deps alone cannot prevent this.
    if (hasRunRef.current) {
      return;
    }
    hasRunRef.current = true;

    props.abortController?.reset();
    props.progressController.reset();
    props.progressController.callback = onProgress;

    const callback = props.callback;
    if (!callback) {
      setLoading(true);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        await callback();
        props.onClose();
      } catch (err) {
        handleError(err);
      } finally {
        setLoading(false);
      }
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAbort = () => {
    props.abortController?.abort();
    props.onClose();
  };

  return (
    <Dialog
      closeIcon={false}
      closeOnDimmerClick={false}
      position={props.position ? { left: props.position.x, top: props.position.y } : 'center'}
      width={props.width ?? 500}
      onClose={onAbort}
    >
      {{
        header: props.title,
        content: (
          <Content>
            {props.children}

            {loading && isDefined(props.progressController) && (
              <ProgressBar height="2em" max={1} min={0} text={progressText} value={progress} />
            )}

            {buildErrorList()}
          </Content>
        ),
        footer: {
          right: (
            <Button
              disabled={!isDefined(props.abortController) || props.abortController?.signal.aborted}
              loading={props.abortController?.signal.aborted}
              variant="text"
              onClick={onAbort}
            >
              {t('cancel', { ns: 'common' })}
            </Button>
          ),
        },
      }}
    </Dialog>
  );
};

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
`;

export { ProgressDialog };
